/**
 * Utility functions to manage ad display and ensure ads are shown only
 * when appropriate (once per day for pre-game, etc.)
 */

/**
 * Checks if the test ads parameter is present in the URL
 * @returns boolean indicating if ads are being tested
 */
export const isTestingAds = (): boolean => {
  if (typeof window === 'undefined') return false;
  
  const urlParams = new URLSearchParams(window.location.search);
  return urlParams.get('testAds') === 'true';
};

/**
 * Checks if pre-game ads should be shown based on:
 * 1. If user is testing ads (testAds=true in URL)
 * 2. If ads haven't been shown today yet
 * @returns boolean indicating if the pre-game ad should be shown
 */
export const shouldShowPreGameAd = (): boolean => {
  // If ads are being tested, we always want to show them
  if (isTestingAds()) {
    return true;
  }
  
  const today = getTodayDateString();
  const lastAdDate = localStorage.getItem('phoodle-pre-game-ad-date');
  
  // If there's no last ad date or it's different from today, show the ad
  if (lastAdDate && lastAdDate !== today) {
    return true;
  }
  
  return false;
};

/**
 * Marks that the pre-game ad has been shown today
 * (Only records this for normal users, not testers)
 */
export const markPreGameAdShown = (): void => {
  // Don't mark ads as shown for test users so they can continue testing
  if (isTestingAds()) {
    return;
  }
  
  const today = getTodayDateString();
  localStorage.setItem('phoodle-pre-game-ad-date', today);
};

/**
 * Gets today's date in YYYY-MM-DD format
 */
export const getTodayDateString = (): string => {
  const d = new Date();
  return (
    d.getFullYear() +
    '-' +
    ('0' + (d.getMonth() + 1)).slice(-2) +
    '-' +
    ('0' + d.getDate()).slice(-2)
  );
};

/**
 * Initializes Google AdSense ads on the page
 */
export const initializeAds = (): void => {
  try {
    if (window.adsbygoogle && typeof window.adsbygoogle.push === 'function') {
      window.adsbygoogle.push({});
    }
  } catch (error) {
    console.error('Error initializing ads:', error);
  }
}; 